import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Calendar } from '@/components/ui/calendar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, Clock, Calendar as CalendarIcon, Plus, Loader2, Settings } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useClinic } from '@/contexts/ClinicContext';
import {
  bookingService,
  type WeeklySchedule,
  type ScheduleException,
  type BookingSettings,
} from '@/services/bookingService';
import {
  BookingLinkCard,
  BookingSettingsForm,
  ScheduleExceptionsList,
  TimezoneSelector,
} from '@/components/booking';

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday', short: 'Sun' },
  { value: 1, label: 'Monday', short: 'Mon' },
  { value: 2, label: 'Tuesday', short: 'Tue' },
  { value: 3, label: 'Wednesday', short: 'Wed' },
  { value: 4, label: 'Thursday', short: 'Thu' },
  { value: 5, label: 'Friday', short: 'Fri' },
  { value: 6, label: 'Saturday', short: 'Sat' },
];

const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const hours = Math.floor(i / 2);
  const minutes = i % 2 === 0 ? '00' : '30';
  const h24 = hours.toString().padStart(2, '0');
  const h12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  const ampm = hours < 12 ? 'AM' : 'PM';
  return {
    value: `${h24}:${minutes}:00`,
    label: `${h12}:${minutes} ${ampm}`,
  };
});

const AvailabilitySettings: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { clinic } = useClinic();

  const goBackTo = location.state?.from || '/';

  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [schedule, setSchedule] = useState<WeeklySchedule[]>([]);
  const [exceptions, setExceptions] = useState<ScheduleException[]>([]);
  const [settings, setSettings] = useState<BookingSettings | null>(null);
  // V2 (identity-first / OTP-verified) default booking URL.
  const [v2DefaultUrl, setV2DefaultUrl] = useState<string>('');
  const [timezone, setTimezone] = useState('America/Chicago');

  // Exception dialog state
  const [exceptionDialogOpen, setExceptionDialogOpen] = useState(false);
  const [exceptionDate, setExceptionDate] = useState<Date | undefined>();
  const [exceptionType, setExceptionType] = useState<'time_off' | 'custom_hours'>('time_off');
  const [exceptionStartTime, setExceptionStartTime] = useState('09:00:00');
  const [exceptionEndTime, setExceptionEndTime] = useState('17:00:00');
  const [exceptionNotes, setExceptionNotes] = useState('');

  // Load data
  const loadData = useCallback(async () => {
    if (!clinic?.id) return;

    setLoading(true);
    try {
      const [availabilityData, settingsData, exceptionsData, v2Links] = await Promise.all([
        bookingService.getAvailability(clinic.id),
        bookingService.getSettings(clinic.id),
        bookingService.getExceptions(clinic.id),
        bookingService.getV2BookingLinks(clinic.id).catch(() => null),
      ]);

      setSchedule(availabilityData);
      setSettings(settingsData);
      setExceptions(exceptionsData);

      // Prefer the practitioner-scoped URL (auto-preselects the provider
      // so V2 skips the chooser). Falls back to the clinic-wide default
      // for admin/staff roles that aren't tied to a single practitioner.
      const preferredUrl = v2Links?.practitioner_default_url || v2Links?.default_url;
      if (preferredUrl) {
        setV2DefaultUrl(preferredUrl);
      }

      if (settingsData?.timezone) {
        setTimezone(settingsData.timezone);
      }
    } catch (error) {
      console.error('Failed to load availability data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load availability settings',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [clinic?.id, toast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle schedule toggle
  const handleDayToggle = (dayOfWeek: number, isAvailable: boolean) => {
    setSchedule(prev => {
      const existing = prev.find(s => s.day_of_week === dayOfWeek);
      if (existing) {
        return prev.map(s =>
          s.day_of_week === dayOfWeek ? { ...s, is_available: isAvailable } : s
        );
      } else {
        return [
          ...prev,
          {
            day_of_week: dayOfWeek,
            start_time: '09:00:00',
            end_time: '17:00:00',
            is_available: isAvailable,
          },
        ];
      }
    });
  };

  // Handle time change
  const handleTimeChange = (dayOfWeek: number, field: 'start_time' | 'end_time', value: string) => {
    setSchedule(prev => {
      const existing = prev.find(s => s.day_of_week === dayOfWeek);
      if (existing) {
        return prev.map(s => (s.day_of_week === dayOfWeek ? { ...s, [field]: value } : s));
      }
      return prev;
    });
  };

  // Save schedule
  const handleSaveSchedule = async () => {
    if (!clinic?.id) return;

    setSaving(true);
    try {
      await bookingService.updateAvailability(clinic.id, schedule);
      toast({
        title: 'Success',
        description: 'Availability schedule saved',
      });
    } catch (error) {
      console.error('Failed to save schedule:', error);
      toast({
        title: 'Error',
        description: 'Failed to save availability schedule',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  // Save settings
  const handleSaveSettings = async () => {
    if (!clinic?.id || !settings) return;

    setSaving(true);
    try {
      await bookingService.updateSettings(clinic.id, settings);
      toast({
        title: 'Success',
        description: 'Booking settings saved',
      });
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to save booking settings',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  // Add exception
  const handleAddException = async () => {
    if (!clinic?.id || !exceptionDate) return;

    try {
      await bookingService.createException(clinic.id, {
        exception_date: format(exceptionDate, 'yyyy-MM-dd'),
        exception_type: exceptionType,
        is_all_day: exceptionType === 'time_off',
        start_time: exceptionType === 'custom_hours' ? exceptionStartTime : undefined,
        end_time: exceptionType === 'custom_hours' ? exceptionEndTime : undefined,
        notes: exceptionNotes,
      });

      toast({
        title: 'Success',
        description: 'Schedule exception added',
      });

      setExceptionDialogOpen(false);
      setExceptionDate(undefined);
      setExceptionNotes('');
      loadData();
    } catch (error) {
      console.error('Failed to add exception:', error);
      const errorMessage =
        (error as { message?: string })?.message || 'Failed to add exception';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  // Delete exception
  const handleDeleteException = async (exceptionId: string) => {
    if (!clinic?.id) return;

    try {
      await bookingService.deleteException(clinic.id, exceptionId);
      toast({
        title: 'Success',
        description: 'Exception removed',
      });
      loadData();
    } catch (error) {
      console.error('Failed to delete exception:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove exception',
        variant: 'destructive',
      });
    }
  };

  // Copy booking link — V2 only. V1 (practitioner-slug) is deprecated; the
  // V1 public route auto-redirects to V2, but admin surfaces should never
  // hand out a V1 URL.
  const handleCopyBookingLink = () => {
    if (!v2DefaultUrl) return;
    navigator.clipboard.writeText(v2DefaultUrl);
    toast({
      title: 'Copied!',
      description: 'Booking link copied to clipboard',
    });
  };

  // Get schedule for a day
  const getScheduleForDay = (dayOfWeek: number) => {
    return (
      schedule.find(s => s.day_of_week === dayOfWeek) || {
        day_of_week: dayOfWeek,
        start_time: '09:00:00',
        end_time: '17:00:00',
        is_available: false,
      }
    );
  };

  // Format time for display
  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    const ampm = h < 12 ? 'AM' : 'PM';
    return `${h12}:${minutes} ${ampm}`;
  };

  if (loading) {
    return (
      <div className='flex min-h-screen items-center justify-center'>
        <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
      </div>
    );
  }

  return (
    <div className='mx-auto max-w-4xl space-y-6 p-6'>
      {/* Header */}
      <div className='flex items-start gap-3'>
        <Button
          variant='outline'
          size='icon'
          onClick={() => navigate(goBackTo)}
          className='h-9 w-9 flex-shrink-0 border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900'
        >
          <ArrowLeft className='h-4 w-4' />
        </Button>
        <div>
          <h1 className='font-montserrat text-2xl font-bold tracking-tight text-slate-900'>
            Availability Settings
          </h1>
          <p className='mt-1 text-sm text-muted-foreground'>
            Manage your weekly schedule, time off, and booking preferences
          </p>
        </div>
      </div>

      {/* Booking Link Card — V2 only. Legacy V1 link surfacing removed;
          patients hitting an old V1 URL are redirected to V2 by the
          public booking page. */}
      {v2DefaultUrl && (
        <BookingLinkCard
          bookingUrl={v2DefaultUrl}
          onCopy={handleCopyBookingLink}
        />
      )}

      {/* Booking Link Toggle */}
      <Card>
        <CardContent className='py-4'>
          <div className='flex items-center justify-between'>
            <div className='space-y-0.5'>
              <Label>Enable Booking Link</Label>
              <p className='text-sm text-muted-foreground'>
                Allow patients to book appointments via this link
              </p>
            </div>
            <Switch
              checked={settings?.booking_link_active ?? true}
              onCheckedChange={checked =>
                setSettings(prev => (prev ? { ...prev, booking_link_active: checked } : null))
              }
            />
          </div>
        </CardContent>
      </Card>

      {/* Weekly Schedule */}
      <Card>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <Clock className='h-5 w-5' />
            Weekly Availability
          </CardTitle>
          <CardDescription>Set your recurring weekly schedule</CardDescription>
        </CardHeader>
        <CardContent className='space-y-4'>
          {/* Timezone Selector */}
          <TimezoneSelector value={timezone} onChange={setTimezone} />

          <Separator />

          {/* Day Schedule */}
          <div className='space-y-3'>
            {DAYS_OF_WEEK.map(day => {
              const daySchedule = getScheduleForDay(day.value);
              return (
                <div key={day.value} className='flex items-center gap-4 rounded-lg border p-3'>
                  <Switch
                    checked={daySchedule.is_available}
                    onCheckedChange={checked => handleDayToggle(day.value, checked)}
                  />
                  <div className='w-24'>
                    <span
                      className={daySchedule.is_available ? 'font-medium' : 'text-muted-foreground'}
                    >
                      {day.label}
                    </span>
                  </div>
                  {daySchedule.is_available ? (
                    <div className='flex flex-1 items-center gap-2'>
                      <Select
                        value={daySchedule.start_time}
                        onValueChange={value => handleTimeChange(day.value, 'start_time', value)}
                      >
                        <SelectTrigger className='w-32'>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TIME_OPTIONS.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <span className='text-muted-foreground'>to</span>
                      <Select
                        value={daySchedule.end_time}
                        onValueChange={value => handleTimeChange(day.value, 'end_time', value)}
                      >
                        <SelectTrigger className='w-32'>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TIME_OPTIONS.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <span className='text-sm text-muted-foreground'>Unavailable</span>
                  )}
                </div>
              );
            })}
          </div>

          <Button onClick={handleSaveSchedule} disabled={saving} className='w-full'>
            {saving ? <Loader2 className='mr-2 h-4 w-4 animate-spin' /> : null}
            Save Schedule
          </Button>
        </CardContent>
      </Card>

      {/* Schedule Exceptions */}
      <Card>
        <CardHeader>
          <div className='flex items-center justify-between'>
            <div>
              <CardTitle className='flex items-center gap-2'>
                <CalendarIcon className='h-5 w-5' />
                Schedule Exceptions
              </CardTitle>
              <CardDescription>Add time off or custom hours for specific dates</CardDescription>
            </div>
            <Dialog open={exceptionDialogOpen} onOpenChange={setExceptionDialogOpen}>
              <DialogTrigger asChild>
                <Button variant='outline' size='sm'>
                  <Plus className='mr-2 h-4 w-4' />
                  Add Exception
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Schedule Exception</DialogTitle>
                  <DialogDescription>
                    Block time off or set custom hours for a specific date
                  </DialogDescription>
                </DialogHeader>
                <div className='space-y-4 py-4'>
                  <div className='space-y-2'>
                    <Label>Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant='outline' className='w-full justify-start text-left'>
                          <CalendarIcon className='mr-2 h-4 w-4' />
                          {exceptionDate ? format(exceptionDate, 'PPP') : 'Select date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className='w-auto p-0' align='start'>
                        <Calendar
                          mode='single'
                          selected={exceptionDate}
                          onSelect={setExceptionDate}
                          disabled={date => date < new Date()}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className='space-y-2'>
                    <Label>Exception Type</Label>
                    <Select
                      value={exceptionType}
                      onValueChange={(v: 'time_off' | 'custom_hours') => setExceptionType(v)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='time_off'>Time Off (Unavailable)</SelectItem>
                        <SelectItem value='custom_hours'>Custom Hours</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {exceptionType === 'custom_hours' && (
                    <div className='flex items-center gap-2'>
                      <div className='flex-1 space-y-2'>
                        <Label>Start Time</Label>
                        <Select value={exceptionStartTime} onValueChange={setExceptionStartTime}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {TIME_OPTIONS.map(opt => (
                              <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className='flex-1 space-y-2'>
                        <Label>End Time</Label>
                        <Select value={exceptionEndTime} onValueChange={setExceptionEndTime}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {TIME_OPTIONS.map(opt => (
                              <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                  <div className='space-y-2'>
                    <Label>Notes (optional)</Label>
                    <Textarea
                      value={exceptionNotes}
                      onChange={e => setExceptionNotes(e.target.value)}
                      placeholder="e.g., Doctor's appointment, vacation, etc."
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant='outline' onClick={() => setExceptionDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleAddException} disabled={!exceptionDate}>
                    Add Exception
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          <ScheduleExceptionsList
            exceptions={exceptions}
            onAdd={() => setExceptionDialogOpen(true)}
            onDelete={handleDeleteException}
            formatTime={formatTime}
          />
        </CardContent>
      </Card>

      {/* Booking Settings */}
      <Card>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <Settings className='h-5 w-5' />
            Booking Settings
          </CardTitle>
          <CardDescription>Configure appointment duration, buffers, and policies</CardDescription>
        </CardHeader>
        <CardContent className='space-y-6'>
          <BookingSettingsForm settings={settings} onChange={setSettings} disabled={saving} />

          <Button onClick={handleSaveSettings} disabled={saving} className='w-full'>
            {saving ? <Loader2 className='mr-2 h-4 w-4 animate-spin' /> : null}
            Save Settings
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default AvailabilitySettings;
